import { FileText } from 'lucide-react'
import { useState } from 'react'

import { Modal } from '@/components/ui/Modal'
import { formatEuros } from '@/lib/format'

/**
 * Het factuurnummer hoort bij de PDF en wordt daarom op hetzelfde moment
 * gevraagd. Het komt terug in het uitbetaaloverzicht, in de export en in de mail
 * die de begunstigde krijgt zodra er is overgemaakt; achteraf aanvullen betekent
 * dat het op al die plekken een tijd ontbreekt.
 */
export function IncomingInvoiceUploadModal({
  file,
  currentNumber,
  share,
  replacing,
  pending,
  onClose,
  onConfirm,
}: {
  file: File
  currentNumber?: string | null
  share: string
  replacing: boolean
  pending: boolean
  onClose: () => void
  onConfirm: (fields: {
    incomingInvoiceNumber: string
    incomingInvoiceAmount: string
    incomingInvoiceTotal: string
    incomingInvoiceDifferenceReason?: string
  }) => void
}) {
  const [number, setNumber] = useState(currentNumber ?? '')
  const [amount, setAmount] = useState('')
  const [total, setTotal] = useState('')
  const [totalTouched, setTotalTouched] = useState(false)
  const [reason, setReason] = useState('')

  const trimmed = number.trim()
  const parsedAmount = parseAmount(amount)
  const parsedTotal = parseAmount(total)
  const difference = parsedAmount === null ? null : Math.round((parsedAmount - Number(share)) * 100) / 100
  const totalTooLow = parsedAmount !== null && parsedTotal !== null && Math.abs(parsedTotal) < Math.abs(parsedAmount)
  // Het totaal is het bedrag inclusief btw. Het tarief ligt niet vast, maar meer
  // dan het hoogste EU-tarief erbij is geen btw meer, dat is een typefout.
  const totalTooHigh =
    parsedAmount !== null && parsedTotal !== null && Math.abs(parsedTotal) > Math.abs(parsedAmount) * 1.27 + 0.005
  const needsReason = difference !== null && difference !== 0
  const complete =
    trimmed !== '' && parsedAmount !== null && parsedTotal !== null && (!needsReason || reason.trim() !== '')
  const vatRate = parsedAmount !== null && parsedTotal !== null ? (parsedTotal / parsedAmount - 1) * 100 : null
  const invalidFields = [
    ...(amount.trim() !== '' && parsedAmount === null ? ['Het bedrag exclusief btw'] : []),
    ...(total.trim() !== '' && parsedTotal === null ? ['het totaal'] : []),
  ]

  function onAmountChange(value: string) {
    setAmount(value)
    if (totalTouched) return
    const parsed = parseAmount(value)
    // In dezelfde notatie als wat je net typte, anders staan er twee schrijfwijzen
    // naast elkaar in een dialoog van twee velden.
    setTotal(
      parsed === null
        ? ''
        : (Math.round(parsed * DEFAULT_VAT * 100) / 100).toLocaleString('nl-NL', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }),
    )
  }

  function submit() {
    if (complete && !totalTooLow && !totalTooHigh && !pending) {
      onConfirm({
        incomingInvoiceNumber: trimmed,
        incomingInvoiceAmount: amount.trim(),
        incomingInvoiceTotal: total.trim(),
        incomingInvoiceDifferenceReason: needsReason ? reason.trim() : undefined,
      })
    }
  }

  return (
    <Modal
      open
      onOpenChange={(o) => !o && onClose()}
      title={replacing ? 'Inkoopfactuur vervangen' : 'Inkoopfactuur koppelen'}
      description="Neem het factuurnummer, het bedrag exclusief btw en het totaal te betalen over van de PDF."
      footer={
        <>
          <button type="button" className="btn-ghost" onClick={onClose}>
            Annuleren
          </button>
          <button type="button" className="btn-primary" onClick={submit} disabled={!complete || totalTooLow || totalTooHigh || pending}>
            {replacing ? 'Vervangen' : 'Koppelen'}
          </button>
        </>
      }
    >
      <div className="flex items-center gap-2 rounded-card border border-hairline px-3 py-2.5">
        <FileText className="h-4 w-4 shrink-0 text-ink-faint" />
        <span className="min-w-0 flex-1 truncate text-sm text-ink">{file.name}</span>
        <span className="money shrink-0 text-xs text-ink-faint">{formatSize(file.size)}</span>
      </div>

      <label className="mt-4 block">
        <span className="label-caps">Factuurnummer</span>
        <input
          type="text"
          value={number}
          onChange={(e) => setNumber(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && submit()}
          autoFocus
          placeholder="bijv. F00093"
          className="field mt-1"
        />
      </label>

      <div className="mt-3 grid grid-cols-2 gap-3">
        <label className="block">
          <span className="label-caps">Bedrag excl. btw</span>
          <input
            type="text"
            inputMode="decimal"
            value={amount}
            onChange={(e) => onAmountChange(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && submit()}
            placeholder="0,00"
            className="field money mt-1"
          />
        </label>
        <label className="block">
          <span className="label-caps">
            Totaal incl. btw{vatRate !== null ? <span className="ml-1 font-normal normal-case tracking-normal text-ink-faint">({formatVatRate(vatRate)} btw)</span> : null}
          </span>
          <input
            type="text"
            inputMode="decimal"
            value={total}
            onChange={(e) => {
              setTotalTouched(true)
              setTotal(e.target.value)
            }}
            onKeyDown={(e) => e.key === 'Enter' && submit()}
            placeholder="0,00"
            className="field money mt-1"
          />
        </label>
      </div>

      {needsReason ? (
        <label className="mt-3 block">
          <span className="label-caps">Waarom wijkt dit af?</span>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={2}
            placeholder="Bijvoorbeeld: een uur minder gefactureerd, in overleg."
            className="field mt-1 resize-none"
          />
        </label>
      ) : null}

      {totalTooHigh ? (
        <p className="mt-3 text-sm text-status-overdue">
          Het totaal is meer dan 27% hoger dan het bedrag exclusief btw. Dat is geen btw-tarief; controleer beide bedragen.
        </p>
      ) : invalidFields.length > 0 ? (
        <p className="mt-3 text-sm text-status-overdue">
          {invalidFields.join(' en ')} {invalidFields.length > 1 ? 'zijn' : 'is'} geen geldig bedrag. Schrijf bijvoorbeeld 1.234,56 of 1234.56.
        </p>
      ) : totalTooLow ? (
        <p className="mt-3 text-sm text-status-overdue">Het totaal kan niet lager zijn dan het bedrag exclusief btw.</p>
      ) : difference !== null && difference !== 0 ? (
        <p className="mt-3 text-sm text-ink-soft">
          Dit is <span className="money">{formatEuros(Math.abs(difference))}</span> {difference > 0 ? 'hoger' : 'lager'} dan het aandeel van{' '}
          <span className="money">{formatEuros(Number(share))}</span>. Leg hierboven uit waarom, dan hoeft degene die accordeert niet te raden.
        </p>
      ) : null}
    </Modal>
  )
}

/**
 * Dezelfde drie vormen die de server accepteert: 1234.56, 1234,56 en 1.234,56.
 * Een losse punt als duizendtalscheiding ("1.200") kan twaalfhonderd of een euro
 * twintig betekenen en wordt daarom geweigerd, hier en op de server.
 */
/** Het gewone geval is 21%; het totaal is voorgevuld en overschrijfbaar. */
const DEFAULT_VAT = 1.21

/**
 * Het afgeleide tarief, zodat je ziet wat je invoer impliceert. Een decimaal is
 * genoeg om 21 van 20,5 te onderscheiden zonder een afrondingsruis te tonen.
 */
export function formatVatRate(rate: number): string {
  return `${(Math.round(rate * 10) / 10).toLocaleString('nl-NL')}%`
}

export function parseAmount(raw: string): number | null {
  const cleaned = raw.replace(/\s/g, '')
  if (cleaned === '') return null

  const normalized = /^-?\d{1,3}(\.\d{3})+,\d{1,2}$/.test(cleaned)
    ? cleaned.replaceAll('.', '').replace(',', '.')
    : /^-?\d+,\d{1,2}$/.test(cleaned)
      ? cleaned.replace(',', '.')
      : /^-?\d+(\.\d{1,2})?$/.test(cleaned)
        ? cleaned
        : null
  if (normalized === null) return null

  const value = Number(normalized)

  return value === 0 || !Number.isFinite(value) ? null : value
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`

  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}
